import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/state/authStore';

export type FeedbackCategory = 'feature' | 'balance' | 'bug' | 'other';
export type FeedbackStatus =
  | 'submitted' | 'shortlist' | 'voting' | 'won' | 'rejected' | 'duplicate';

export interface FeedbackItem {
  id: string;
  authorId: string;
  authorName: string;
  category: FeedbackCategory;
  title: string;
  body: string;
  status: FeedbackStatus;
  upvotes: number;
  voteRoundId: string | null;
  createdAt: number;
}

export interface VoteRound {
  id: string;
  title: string;
  description: string;
  startsAt: number;
  endsAt: number;
  winnerFeedbackId: string | null;
  status: 'active' | 'finished';
}

export interface RoundTally {
  feedbackId: string;
  votes: number;
}

export type FeedbackSort = 'newest' | 'top';

interface FeedbackViewRow {
  id: string;
  author_id: string;
  author_name: string;
  category: string;
  title: string;
  body: string;
  status: string;
  upvotes: number;
  vote_round_id: string | null;
  created_at: string;
}

interface VoteRoundRow {
  id: string;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  winner_feedback_id: string | null;
  status: string;
}

function rowToFeedback(r: FeedbackViewRow): FeedbackItem {
  return {
    id: r.id,
    authorId: r.author_id,
    authorName: r.author_name ?? '?',
    category: r.category as FeedbackCategory,
    title: r.title,
    body: r.body,
    status: r.status as FeedbackStatus,
    upvotes: r.upvotes,
    voteRoundId: r.vote_round_id,
    createdAt: new Date(r.created_at).getTime(),
  };
}

function rowToRound(r: VoteRoundRow): VoteRound {
  return {
    id: r.id,
    title: r.title,
    description: r.description ?? '',
    startsAt: new Date(r.starts_at).getTime(),
    endsAt: new Date(r.ends_at).getTime(),
    winnerFeedbackId: r.winner_feedback_id,
    status: r.status as 'active' | 'finished',
  };
}

interface FeedbackState {
  items: FeedbackItem[];
  myUpvoteIds: Set<string>;

  activeRound: VoteRound | null;
  roundCandidates: FeedbackItem[];
  roundTallies: Record<string, number>;
  myRoundVote: string | null; // feedback_id

  loading: boolean;
  error: string | null;

  sort: FeedbackSort;
  filterCategory: FeedbackCategory | null;

  loadAll: () => Promise<void>;
  submit: (input: {
    category: FeedbackCategory;
    title: string;
    body: string;
  }) => Promise<{ ok: boolean; error?: string }>;
  toggleUpvote: (feedbackId: string) => Promise<void>;
  castRoundVote: (feedbackId: string) => Promise<{ ok: boolean; error?: string }>;
  setSort: (s: FeedbackSort) => void;
  setFilterCategory: (c: FeedbackCategory | null) => void;
}

export const useFeedbackStore = create<FeedbackState>()((set, get) => ({
  items: [],
  myUpvoteIds: new Set<string>(),
  activeRound: null,
  roundCandidates: [],
  roundTallies: {},
  myRoundVote: null,
  loading: false,
  error: null,
  sort: 'top',
  filterCategory: null,

  setSort: (sort) => set({ sort }),
  setFilterCategory: (filterCategory) => set({ filterCategory }),

  loadAll: async () => {
    set({ loading: true, error: null });
    const userId = useAuthStore.getState().user?.id;

    // 1. List of all feedback (any status), with author names
    const { data: items, error: e1 } = await supabase
      .from('feedback_view')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);
    if (e1) {
      set({ loading: false, error: e1.message });
      return;
    }

    // 2. My upvotes
    let myUpvoteIds = new Set<string>();
    if (userId) {
      const { data: myVotes } = await supabase
        .from('feedback_votes')
        .select('feedback_id')
        .eq('voter_id', userId);
      myUpvoteIds = new Set((myVotes ?? []).map((r) => r.feedback_id as string));
    }

    // 3. Active vote round (if any) + its candidates + tallies
    const { data: rounds } = await supabase
      .from('vote_rounds')
      .select('*')
      .eq('status', 'active')
      .order('starts_at', { ascending: false })
      .limit(1);

    let activeRound: VoteRound | null = null;
    let roundCandidates: FeedbackItem[] = [];
    let roundTallies: Record<string, number> = {};
    let myRoundVote: string | null = null;

    if (rounds && rounds.length > 0) {
      activeRound = rowToRound(rounds[0] as VoteRoundRow);

      const { data: cands } = await supabase
        .from('feedback_view')
        .select('*')
        .eq('vote_round_id', activeRound.id);
      roundCandidates = (cands as FeedbackViewRow[] | null ?? []).map(rowToFeedback);

      const { data: votes } = await supabase
        .from('round_votes')
        .select('feedback_id, voter_id')
        .eq('round_id', activeRound.id);
      for (const v of (votes ?? []) as Array<{ feedback_id: string; voter_id: string }>) {
        roundTallies[v.feedback_id] = (roundTallies[v.feedback_id] ?? 0) + 1;
        if (userId && v.voter_id === userId) myRoundVote = v.feedback_id;
      }
    }

    set({
      items: ((items as FeedbackViewRow[] | null) ?? []).map(rowToFeedback),
      myUpvoteIds,
      activeRound,
      roundCandidates,
      roundTallies,
      myRoundVote,
      loading: false,
    });
  },

  submit: async ({ category, title, body }) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return { ok: false, error: 'not_authenticated' };
    const { error } = await supabase.from('feedback').insert({
      author_id: userId,
      category,
      title: title.trim(),
      body: body.trim(),
    });
    if (error) return { ok: false, error: error.message };
    await get().loadAll();
    return { ok: true };
  },

  toggleUpvote: async (feedbackId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;
    const had = get().myUpvoteIds.has(feedbackId);
    if (had) {
      const { error } = await supabase
        .from('feedback_votes')
        .delete()
        .eq('feedback_id', feedbackId)
        .eq('voter_id', userId);
      if (error) {
        set({ error: error.message });
        return;
      }
    } else {
      const { error } = await supabase
        .from('feedback_votes')
        .insert({ feedback_id: feedbackId, voter_id: userId });
      if (error) {
        set({ error: error.message });
        return;
      }
    }
    // Optimistic local update + refetch upvote count for accuracy
    set((s) => {
      const next = new Set(s.myUpvoteIds);
      if (had) next.delete(feedbackId);
      else next.add(feedbackId);
      return {
        myUpvoteIds: next,
        items: s.items.map((it) =>
          it.id === feedbackId
            ? { ...it, upvotes: Math.max(0, it.upvotes + (had ? -1 : 1)) }
            : it,
        ),
      };
    });
  },

  castRoundVote: async (feedbackId) => {
    const round = get().activeRound;
    if (!round) return { ok: false, error: 'no_active_round' };
    if (get().myRoundVote) return { ok: false, error: 'already_voted' };
    const { data, error } = await supabase.rpc('cast_round_vote', {
      p_round_id: round.id,
      p_feedback_id: feedbackId,
    });
    if (error) return { ok: false, error: error.message };
    const result = data as { ok?: boolean; error?: string };
    if (!result?.ok) return { ok: false, error: result?.error ?? 'unknown' };

    set((s) => ({
      myRoundVote: feedbackId,
      roundTallies: {
        ...s.roundTallies,
        [feedbackId]: (s.roundTallies[feedbackId] ?? 0) + 1,
      },
    }));
    return { ok: true };
  },
}));

/** Sorted + filtered for the "Vorschläge" tab. */
export function useBrowseFeedback(): FeedbackItem[] {
  const items = useFeedbackStore((s) => s.items);
  const sort = useFeedbackStore((s) => s.sort);
  const filterCategory = useFeedbackStore((s) => s.filterCategory);

  let out = items.filter(
    (it) => it.status === 'submitted' || it.status === 'shortlist',
  );
  if (filterCategory) out = out.filter((it) => it.category === filterCategory);
  out = [...out].sort((a, b) =>
    sort === 'top' ? b.upvotes - a.upvotes : b.createdAt - a.createdAt,
  );
  return out;
}

/** Items with status='won' for hall-of-fame display. */
export function useWinners(): FeedbackItem[] {
  const items = useFeedbackStore((s) => s.items);
  return items.filter((it) => it.status === 'won');
}
